/** 
 * ======================================================================== 
 * RMS Navigation (Header, Footer, G-Navi) @{versionFull}
 * ======================================================================== 
 * 
 * ======================================================================== 
 * Author: ナカカド リー (Lee Nakakead)
 * ======================================================================== 
 */
import { IFooter, initFooterModule, renderFooter, IFooterList } from './_footer';
import { IHeader, initHeaderModule, renderHeader, IHeaderNavigation } from './_header';
import { IGNavi, initGNaviModule, initGNaviError, renderGNavi } from './_gnavi';

class RMSNavi {
  private SETTINGS_HEADER: IHeader = {
    target: '#rms-header',
    targetGroup: '.rms-header-group',
    classNames: ['rms-header'],
    classLoadSpinner: 'load-spinner',
    classGroupHelp: 'rms-header-group-help',
    classGroupEcc: 'rms-header-group-ecc',
    classGroupShop: 'rms-header-group-shop',
    classGroupShopPageButton: 'rms-btn btn-blue btn-md btn-block',
    classPopover: 'rms-header-popover',
    classPopoverButton: 'rms-header-popover-btn',
    classPopoverBody: 'rms-header-popover-body',
    classPopoverContent: 'rms-header-popover-content',
    classPopoverContentItem: 'rms-header-popover-content-item',
    classColumns: 'rms-header-columns',
    classColumnsCol: 'rms-header-columns-col',
    classColumnsColAuto: 'rms-header-columns-col-auto',
    classGroupEccImg: 'rms-header-group-ecc-img',
    classGroupShopImg: 'rms-header-group-shop-img',
    classGroupShopName: 'rms-header-group-shop-name',
    classGroupShopIcon: 'rms-header-group-shop-icon',
    classMarginBottom: 'ma-b-10',
    classMarginLeft: 'ma-l-6',
    classActive: 'active',
    labelGroupEccTelephone: 'TEL：',
    labelGroupShopURL: '店舗URL：',
    labelGroupShopStatus: 'ステータス：',
    labelGroupShopPlan: '契約プラン：',
  };

  private SETTINGS_FOOTER: IFooter = {
    target: '#rms-footer',
    classNames: ['rms-footer'],
    copyrightTexts: ['Rakuten Merchant Server', ' © Rakuten Group, Inc.'],
    separator: ' :&nbsp',
  };

  private SETTINGS_GNAVI: IGNavi = {
    target: '#rms-navi-global',
    classNames: ['rms-navi-global'],
    classActive: 'active',
    classDisabled: 'disabled',
    classOpen: 'open',
    classSelected: 'selected',
    classIcon: 'rms-icon',
    classContentCategories: 'rms-navi-global-content-categories',
    classContentCategoryItem: 'category-item',
    classContentSubCategories: 'rms-navi-global-content-subcategories',
    classContentSubcategory: 'subcategory',
    classContentSubcategoryOpen: 'open',
    classContentSubcategoryAccordion: 'accordion',
    classContentSubcategoryAccordionOpen: 'open',
    classContentSubcategoryAccordionMenu: 'accordion-menu',
    classContentSubcategoryAccordionToggle: 'accordion-toggle',
    classContentSubcategoryItem: 'category-item',
    classContentSubcategoryItemNumber: 'category-number',
    classToggler: 'rms-navi-global-toggler',
    classBackdrop: 'rms-navi-global-backdrop',
    classBackdropButton: 'rms-navi-global-backdrop-button',
    classBackdropButtonContent: 'rms-navi-global-backdrop-button-content',
    classDataError: 'rms-navi-global-error',
    labelTogglerTooltipTitleOpen: 'メニューを閉じる',
    labelTogglerTooltipTitleClosed: 'メニューを開く',
    labelBackdropButton: '閉じる',
    labelDataError: 'メニューが表示できませんでした。再読み込みしてください。',
  };

  // private data: any;
  private settings: ISettings;
  
  constructor(settings: ISettings) {
    const header = settings.header ? { ...this.SETTINGS_HEADER, ...settings.header } : this.SETTINGS_HEADER;
    const footer = settings.footer ? { ...this.SETTINGS_FOOTER, ...settings.footer } : this.SETTINGS_FOOTER;
    const gnavi = settings.gnavi ? { ...this.SETTINGS_GNAVI, ...settings.gnavi } : this.SETTINGS_GNAVI;
    const data = settings.data;
    let target = '';
    if (settings.target) {
      target = settings.target;
    } else {
      const gnaviTargetElement = document.querySelector('[data-gnavi-target]');
      if (gnaviTargetElement) target = (gnaviTargetElement as HTMLElement).dataset.gnaviTarget || '';
    }

    this.settings = {
      header,
      footer,
      gnavi,
      target,
      data,
    };
  }

  /** Initialize Navi */
  public async init(modules?: ModuleType) {
    const { footer, header, gnavi, data: dataResult } = this.settings;
    const { target: targetHeader } = header as IHeader;
    const { target: targetFooter } = footer as IFooter;
    const { target: targetGNavi } = gnavi as IGNavi;

    switch (modules) {
      case ModuleEnum.header: {
        this.initModule(modules as Module, targetHeader);
        break;
      }
      case ModuleEnum.footer: {
        this.initModule(modules as Module, targetFooter);
        break;
      }
      case ModuleEnum.gnavi: {
        this.initModule(modules as Module, targetGNavi);
        break;
      }
      case ModuleEnum.all: {
        this.initModule('header' as Module, targetHeader);
        this.initModule('footer' as Module, targetFooter);
        this.initModule('gnavi' as Module, targetGNavi);
        break;
      }
      default: {
        if (!Array.isArray(modules)) break;
        const uniqueModules = modules.filter((module, index) => modules.indexOf(module) === index);
        for (const module of uniqueModules) {
          switch (module) {
            case ModuleEnum.header: {
              this.initModule(module, targetHeader);
              continue;
            }
            case ModuleEnum.footer: {
              this.initModule(module, targetFooter);
              continue;
            }
            case ModuleEnum.gnavi: {
              this.initModule(module, targetGNavi);
              continue;
            }
            default: {
              continue; 
            }
          }
        }
        break;
      }
    }

    if (!dataResult.results) {
      this.failOnInit(dataResult.errors);
      if (modules === ModuleEnum.gnavi || modules === ModuleEnum.all) initGNaviError(this.settings as ISettings);
      return;
    } else {
      // Handle successfull results.
      const data = dataResult.results[0];
      switch (modules) {
        case ModuleEnum.header: {
          renderHeader(this.settings, data.headerNavigation);
          return;
        }
        case ModuleEnum.footer: {
          renderFooter(this.settings, data.footerNavigation);
          return;
        }
        case ModuleEnum.gnavi: {
          renderGNavi(this.settings, data.leftNavigation);
          return;
        }
        case ModuleEnum.all: {
          renderHeader(this.settings, data.headerNavigation);
          renderFooter(this.settings, data.footerNavigation);
          renderGNavi(this.settings, data.leftNavigation);
          return;
        }
        default: {
          if (!Array.isArray(modules)) return;
          const uniqueModules = modules.filter((module, index) => modules.indexOf(module) === index);
          for (const module of uniqueModules) {
            switch (module) {
              case ModuleEnum.header: {
                renderHeader(this.settings, data.headerNavigation);
                continue;
              }
              case ModuleEnum.footer: {
                renderFooter(this.settings, data.footerNavigation);
                continue;
              }
              case ModuleEnum.gnavi: {
                renderGNavi(this.settings, data.leftNavigation);
                continue;
              }
              default: {
                continue; 
              }
            }
          }
          return;
        }
      }
    }
  }

  /** Initialize module */
  private initModule(module: Module, target: string) {
    const { classLoadSpinner } = this.settings.header as IHeader;
    const element = document.querySelector(target as string);
    if (!element) return;
    /** Add load-spinner */
    const loadSpinner = document.createElement('div');
    loadSpinner.className = classLoadSpinner;
    const loadSpinnerIcon = document.createElement('i');
    loadSpinnerIcon.className = 'rms-ui-icon-automation';
    loadSpinner.appendChild(loadSpinnerIcon);
    element.appendChild(loadSpinner);
    if (module === 'footer') initFooterModule(this.settings, target);
    if (module === 'header') initHeaderModule(this.settings, target);
    if (module === 'gnavi') initGNaviModule(this.settings, target);
  }

  /** Failure during the initialization of data */
  private failOnInit(errors: IError[]) {
    const { footer, header, gnavi } = this.settings;
    const { target: targetHeader } = header as IHeader;
    const { target: targetFooter } = footer as IFooter;
    const { target: targetGNavi } = gnavi as IGNavi;
    const headerContent = document.querySelector(targetHeader as string);
    const footerContent = document.querySelector(targetFooter as string);
    const gnaviContent = document.querySelector(targetGNavi as string);
    if (headerContent) removeLoadSpinner(this.settings as ISettings, headerContent);
    if (footerContent) removeLoadSpinner(this.settings as ISettings, footerContent);
    if (gnaviContent) removeLoadSpinner(this.settings as ISettings, gnaviContent);
    if (errors.length) {
      for (const error of errors) {
        console.error(`ERROR ${error.code}: ${error.message}`);
      }
    }
  }
}

/** Remove Load Spinner */
export function removeLoadSpinner(settings: ISettings, parentElement: Element) {
  const { classLoadSpinner } = settings.header as IHeader;
  const loadSpinner = parentElement.querySelector(`.${classLoadSpinner}`);
  if (loadSpinner) parentElement.removeChild(loadSpinner);
}

/** Get anchor properties as string */
export function getStringAnchorProperties(anchor: IDataItem): string {
  const { canAccess, url, id, newTab } = anchor;
  const properties: string[] = [];
  if (canAccess && url) properties.push(`href="${url}"`);
  if (newTab) properties.push(`target="_blank"`);
  if (id) properties.push(`id="${id}" data-ratid="${id}" data-ratevent="click" data-ratparam="all" triforcev2status="done"`);
  return properties.join(' ');
}

/** Get button properties as string */
export function getStringButtonProperties(btn: IDataItem): string {
  const properties: string[] = [];
  if (btn.id) properties.push(`id="${btn.id}" data-ratid="${btn.id}" data-ratevent="click" data-ratparam="all" triforcev2status="done"`);
  return properties.join(' ');
}

export interface ISettings {
  header?: IHeader;
  footer?: IFooter;
  gnavi?: IGNavi;
  target?: string;
  data: IData;
}

const enum ModuleEnum {
  header = 'header',
  footer = 'footer',
  gnavi = 'gnavi',
  all = 'all',
}

type Module = 'header' | 'footer' | 'gnavi';
type ModuleType = Module | Module[] | 'all';

interface IData {
  errors?: IError[];
  results?: IDataResult;
}

interface IError {
  code?: string;
  message?: string;
  detailedMessage?: string;
}

export interface IDataResult {
  headerNavigation?: IHeaderNavigation;
  leftNavigation?: IDataItem[];
  footerNavigation?: IFooterList[];
}

export interface IDataItem {
  id: string;
  title?: string[];
  icon?: string;
  image?: string;
  url?: string;
  newTab?: boolean;
  canAccess?: boolean;
  list?: IDataItem[];
}

window['RMSNavi'] = RMSNavi;